import React, { useEffect, useState } from "react";
import "./CourseModal.css";
import Select from "../../../../components/reusable-select/Select";
import InputBox from "../../../../components/reusable-input/InputBox";
import Button from "../../../../components/reusable-button/Button";
import Switch from "../../../../components/reusable-switch/Switch";
import { FiPlusCircle } from "react-icons/fi";
import { MdDeleteForever } from "react-icons/md";

const DeleteIcon = MdDeleteForever as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;

export interface CourseLink {
  linkDisplayName: string | null;
  url: string | null;
}

export interface CourseFormData {
  categoryType: string;
  category: string;
  courseName: string;
  description: string;
  active: boolean;
  courseLearningLink: CourseLink[];
}

interface CourseModalProps {
  isOpen: boolean;
  mode: "add" | "edit";
  onClose: () => void;
  onSave: (data: CourseFormData) => void;
  initialData?: CourseFormData;
  categoryTypeOptions: { label: string; value: string }[];
  categoryOptions: { label: string; value: string; categoryTypeId: string }[];
  loading?: boolean;
}

const PlusIcon = FiPlusCircle as unknown as React.FC<
  React.SVGProps<SVGSVGElement>
>;

const CourseModal: React.FC<CourseModalProps> = ({
  isOpen,
  mode,
  onClose,
  onSave,
  initialData,
  categoryTypeOptions,
  categoryOptions,
  loading = false,
}) => {
  const [form, setForm] = useState<CourseFormData>({
    categoryType: "",
    category: "",
    courseName: "",
    description: "",
    active: true,
    courseLearningLink: [{ linkDisplayName: null, url: null }],
  });

  useEffect(() => {
    if (!isOpen) {
      setForm({
        categoryType: "",
        category: "",
        courseName: "",
        description: "",
        active: true,
        courseLearningLink: [{ linkDisplayName: null, url: null }],
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (initialData) {
      setForm(initialData);
    }
  }, [initialData]);

  const handleChange = (key: keyof CourseFormData, value: any) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const cleanedLinks = form.courseLearningLink
      .map((link) => ({
        linkDisplayName: (link.linkDisplayName ?? "").trim() || null,
        url: (link.url ?? "").trim() || null,
      }))
      .filter((link) => link.linkDisplayName && link.url); // Only keep valid ones

    onSave({ ...form, courseLearningLink: cleanedLinks });
  };

  const handleLinkChange = (
    index: number,
    key: keyof CourseLink,
    value: string
  ) => {
    const updatedLinks = [...form.courseLearningLink];
    updatedLinks[index][key] = value;
    setForm((prev) => ({ ...prev, courseLearningLink: updatedLinks }));
  };

  const addLinkRow = () => {
    setForm((prev) => ({
      ...prev,
      courseLearningLink: [
        ...prev.courseLearningLink,
        { linkDisplayName: null, url: null },
      ],
    }));
  };

  const handleModalClose = () => {
    setForm({
      categoryType: "",
      category: "",
      courseName: "",
      description: "",
      active: true,
      courseLearningLink: [{ linkDisplayName: null, url: null }],
    });
    onClose();
  };

  const filteredCategoryOptions = categoryOptions.filter(
    (c) => c.categoryTypeId === form.categoryType
  );

  const removeLinkRow = (indexToRemove: number) => {
    const updatedLinks = form.courseLearningLink.filter(
      (_, i) => i !== indexToRemove
    );
    setForm((prev) => ({
      ...prev,
      courseLearningLink: updatedLinks,
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="course-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="course-modal-title">
          {mode === "add" ? "Add Course" : "Edit Course"}
        </h2>

        <form onSubmit={handleSubmit} className="modal-body">
          <Select
            label="Select Category Type"
            name="category-type"
            required
            options={categoryTypeOptions}
            value={form.categoryType}
            onChange={(e) => {
              const newCategoryType = e.target.value;
              handleChange("categoryType", newCategoryType);
              handleChange("category", "");
            }}
          />

          <Select
            label="Select Category"
            name="category"
            required
            options={filteredCategoryOptions}
            value={form.category}
            onChange={(e) => handleChange("category", e.target.value)}
          />

          <InputBox
            label="Course Name"
            required
            placeholder="Enter Course Name"
            value={form.courseName}
            onChange={(e) => handleChange("courseName", e.target.value)}
            maxLength={120}
          />

          <label className="input-label">
            Description <span className="required">*</span>
          </label>
          <textarea
            className="textarea"
            required
            placeholder="Enter Description"
            value={form.description}
            onChange={(e) => handleChange("description", e.target.value)}
          />

          <div
            className="link-main"
            style={{ fontWeight: "bold", marginTop: "1rem" }}
          >
            Course Link
            <span
              style={{ cursor: "pointer", fontSize: "1.2rem" }}
              onClick={addLinkRow}
            >
              <PlusIcon />
            </span>
          </div>

          {form?.courseLearningLink?.map((link, index) => (
            <React.Fragment key={index}>
              <div
                style={{
                  display: "flex",
                  gap: "1rem",
                  marginBottom: "8px",
                  alignItems: "flex-end",
                }}
              >
                <div style={{ width: "45%" }}>
                  <InputBox
                    label="Link Name"
                    placeholder="Enter Link Name"
                    value={link.linkDisplayName ?? ""}
                    onChange={(e) =>
                      handleLinkChange(index, "linkDisplayName", e.target.value)
                    }
                    maxLength={120}
                  />
                </div>
                <div style={{ width: "45%" }}>
                  <InputBox
                    label="Course URL"
                    placeholder="Enter Course URL"
                    value={link.url ?? ""}
                    onChange={(e) =>
                      handleLinkChange(index, "url", e.target.value)
                    }
                  />
                </div>

                <DeleteIcon
                  onClick={() => removeLinkRow(index)}
                  style={{
                    cursor: "pointer",
                    fontSize: "20px",
                    marginBottom: "18px",
                  }}
                />
              </div>
            </React.Fragment>
          ))}
          {initialData && (
            <Switch
              label={form.active ? "Active" : "Inactive"}
              checked={form.active}
              onChange={() =>
                setForm((prev) => ({ ...prev, active: !prev.active }))
              }
            />
          )}

          <div className="course-footer">
            <Button
              variant="outlined"
              label="Cancel"
              onClick={handleModalClose}
              type="button"
            />
            <Button
              label={mode === "add" ? "Add Course" : "Update Course"}
              type="submit"
              loading={loading}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default CourseModal;
